/**
 * /decodificar — Perfil do Cliente.
 *
 * Two modes (URL-controlled via `?mode=quiz`):
 *   1. Default (no query): header + 9-type grid + history list + mini dashboard.
 *   2. Express mode: 5 observational signal chips → instant playbook + save.
 *
 * Outer structure identical to the original DEMO.4 page. Only QuizFlow was
 * replaced by the express 5-signal decode (no 135-question quiz).
 *
 * Source method: docs/method/rafa-medeiros/v1/pillars/09-nove-personalidades.md
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  ArrowClockwise,
  ArrowRight,
  CaretDown,
  Clock,
  Crosshair,
  FloppyDisk,
  MagnifyingGlass,
  Sparkle,
} from "@phosphor-icons/react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { PageStateGate } from "@/components/states";
import { EnneagramBadge } from "@/components/shared/EnneagramBadge";
import { DeleteEntityButton } from "@/components/shared/DeleteEntityButton";
import { BulkActionsBar } from "@/components/shared/BulkActionsBar";
import { Checkbox } from "@/components/ui/checkbox";
import { usePageTitle } from "@/hooks/usePageTitle";
import { toast } from "@/lib/toast";
import {
  TYPE_ACCENT,
  TYPE_CONTENT,
  getTypeContent,
} from "@/features/decodificar/content";
import {
  useDeleteExpress,
  useExpressHistory,
  useRenameExpress,
  useSaveExpress,
  useUpdateExpress,
} from "@/features/decodificar/hooks/useExpress";
import { InlineEditText } from "@/components/shared/InlineEditText";
import { SIGNALS } from "@/features/decodificar/express/signals";
import {
  answeredSignalCount,
  classify,
} from "@/features/decodificar/express/classifier";
import { SignalChips } from "@/components/decodificar/SignalChips";
import { PlaybookCard } from "@/components/decodificar/PlaybookCard";
import type {
  ClassifyResult,
  ExpressAnswers,
} from "@/features/decodificar/express/types";
import type { EnneagramTypeId } from "@/types/supabase";
import type { DecodificarExpressRow } from "@/features/decodificar/api/express";
import { HelpButton } from "@/components/ui/help-button";

const MODE_QUIZ = "quiz" as const;

export default function Decodificar() {
  usePageTitle("Perfil do Cliente");
  const [searchParams, setSearchParams] = useSearchParams();
  const mode = searchParams.get("mode");

  const [readMoreType, setReadMoreType] = useState<EnneagramTypeId | null>(null);
  const [editRow, setEditRow] = useState<DecodificarExpressRow | null>(null);

  const enterQuiz = () => {
    const next = new URLSearchParams(searchParams);
    next.set("mode", MODE_QUIZ);
    setSearchParams(next);
  };

  const exitQuiz = () => {
    setEditRow(null);
    const next = new URLSearchParams(searchParams);
    next.delete("mode");
    setSearchParams(next);
  };

  const openEdit = (row: DecodificarExpressRow) => {
    setEditRow(row);
    enterQuiz();
  };

  return (
    <AppLayout>
      <div className="space-y-8 animate-fade-in">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold text-foreground">
              Perfil do <span className="accent-text">Cliente</span>
            </h1>
            <HelpButton
              title="Perfil do Cliente"
              description="Decodifique a personalidade do lead/cliente para conduzir a venda do jeito certo."
            >
              <p>
                Cada pessoa decide de uma forma diferente. O Perfil do Cliente
                identifica o <strong>Código (C1–C9)</strong> dominante do seu
                lead a partir de um quiz curto.
              </p>
              <p>
                <strong>Como usar:</strong> rode o quiz (~60s) com base no que
                você já sabe do lead — fala dele, mensagens, perfil. O sistema
                devolve o código + dor central + roteiro de condução.
              </p>
              <p className="text-muted-foreground">
                Etapa do método: diagnóstico inicial, antes da consulta ou da
                primeira call de venda.
              </p>
            </HelpButton>
          </div>
          <p className="text-muted-foreground mt-1">
            Descubra o código de Eneagrama do seu cliente e saiba como conduzi-lo.
          </p>
        </div>

        {mode === MODE_QUIZ ? (
          <QuizFlow onExit={exitQuiz} initialRow={editRow} />
        ) : (
          <ListingView
            onStartQuiz={enterQuiz}
            onReadMoreType={setReadMoreType}
            onOpenResult={openEdit}
          />
        )}
      </div>

      <Sheet
        open={readMoreType !== null}
        onOpenChange={(open) => !open && setReadMoreType(null)}
      >
        <SheetContent side="right" className="overflow-y-auto sm:max-w-md">
          {/* A23-A11y — Radix requires DialogTitle even when content is gated.
              TypeDetailContent renders the visible SheetTitle; this fallback
              prevents a missing-title warning during the open transition. */}
          <VisuallyHidden>
            <SheetTitle>Detalhes do tipo</SheetTitle>
          </VisuallyHidden>
          {readMoreType !== null ? (
            <TypeDetailContent typeId={readMoreType} />
          ) : null}
        </SheetContent>
      </Sheet>
    </AppLayout>
  );
}

// ─── Listing ──────────────────────────────────────────────────────────────

interface ListingViewProps {
  onStartQuiz: () => void;
  onReadMoreType: (id: EnneagramTypeId) => void;
  onOpenResult: (row: DecodificarExpressRow) => void;
}

function ListingView({ onStartQuiz, onReadMoreType, onOpenResult }: ListingViewProps) {
  const historyQuery = useExpressHistory(20);
  const deleteExpress = useDeleteExpress();
  const renameExpress = useRenameExpress();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const stats = useMemo(
    () => buildDecodificarStats(historyQuery.data ?? []),
    [historyQuery.data],
  );

  useEffect(() => {
    setSelectedIds((prev) => {
      const validIds = new Set((historyQuery.data ?? []).map((row) => row.id));
      const next = new Set<string>();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [historyQuery.data]);

  function toggleSelection(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function handleBulkDelete() {
    if (selectedIds.size === 0) return;
    setBulkDeleting(true);
    const ids = Array.from(selectedIds);
    const results = await Promise.allSettled(
      ids.map((id) => deleteExpress.mutateAsync(id)),
    );
    const success = results.filter((r) => r.status === "fulfilled").length;
    const failed = results.length - success;
    if (success > 0) toast.success(`${success} análise(s) excluída(s).`);
    if (failed > 0) toast.error(`${failed} análise(s) não puderam ser excluídas.`);
    setSelectedIds(new Set());
    setBulkDeleting(false);
  }

  return (
    <div className="space-y-8">
      {/* Hero / start CTA */}
      <div className="bg-card border border-border/50 rounded-xl p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
        <div className="relative z-10 flex flex-col md:flex-row items-center gap-6">
          <div className="p-4 rounded-lg bg-primary/10">
            <Crosshair weight="duotone" className="w-12 h-12 text-primary" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h2 className="text-xl font-semibold text-foreground mb-2">
              Nova Decodificação Rápida
            </h2>
            <p className="text-muted-foreground max-w-lg">
              Observe 5 sinais do paciente e descubra seu{" "}
              <span className="accent-text">Código (C1–C9)</span> em menos de
              60 segundos. Receba o playbook de condução instantaneamente.
            </p>
          </div>
          <Button
            size="lg"
            className="gap-2 glow-primary-sm w-full md:w-auto"
            onClick={onStartQuiz}
          >
            Nova análise
            <ArrowRight weight="bold" className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Mini dashboard with type distribution */}
      <DecodificarMiniDashboard stats={stats} />

      {/* 9-type grid */}
      <div>
        <h2 className="text-lg font-semibold text-foreground mb-4">
          Os 9 Códigos do Eneagrama
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 2xl:grid-cols-9 gap-2">
          {(Object.keys(TYPE_CONTENT) as Array<`${EnneagramTypeId}`>)
            .map((k) => Number(k) as EnneagramTypeId)
            .sort((a, b) => a - b)
            .map((id) => (
              <button
                key={id}
                type="button"
                onClick={() => onReadMoreType(id)}
                className="rounded-xl transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                // A17-A11y — visible badge text is `{name}`; place it as a
                // prefix in the accessible name so Voice Control "click {name}"
                // matches (WCAG 2.5.3 Label in Name).
                aria-label={`${TYPE_CONTENT[id].name} — ver detalhes do código C${id}`}
              >
                <EnneagramBadge
                  type={id}
                  label={TYPE_CONTENT[id].name}
                  accentColor={TYPE_ACCENT[id]}
                  className="w-full"
                />
              </button>
            ))}
        </div>
      </div>

      {/* History list */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Histórico de análises
          </h2>
        </div>
        <BulkActionsBar
          totalCount={historyQuery.data?.length ?? 0}
          selectedCount={selectedIds.size}
          allSelected={
            (historyQuery.data?.length ?? 0) > 0 &&
            selectedIds.size === (historyQuery.data?.length ?? 0)
          }
          selectionMode={selectionMode}
          deleting={bulkDeleting}
          entityPluralLabel="análises"
          onToggleSelectionMode={(enabled) => {
            setSelectionMode(enabled);
            if (!enabled) setSelectedIds(new Set());
          }}
          onToggleSelectAll={(checked) => {
            if (!checked) {
              setSelectedIds(new Set());
              return;
            }
            setSelectedIds(new Set((historyQuery.data ?? []).map((row) => row.id)));
          }}
          onClearSelection={() => setSelectedIds(new Set())}
          onConfirmDelete={handleBulkDelete}
        />
        <PageStateGate
          query={{
            isPending: historyQuery.isPending,
            isError: historyQuery.isError,
            error: historyQuery.error,
            data: historyQuery.data,
            refetch: () => void historyQuery.refetch(),
          }}
          empty={{
            icon: (
              <MagnifyingGlass
                weight="duotone"
                className="h-6 w-6"
                aria-hidden="true"
              />
            ),
            title: "Nenhuma análise ainda",
            description: "Cadastre sua primeira análise para começar.",
            action: (
              <Button className="gap-2" onClick={onStartQuiz}>
                Nova análise
              </Button>
            ),
          }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(historyQuery.data ?? []).map((row) => (
              <HistoryCard
                key={row.id}
                row={row}
                selectionMode={selectionMode}
                selected={selectedIds.has(row.id)}
                onToggleSelected={(checked) => toggleSelection(row.id, checked)}
                disableActions={bulkDeleting}
                onClick={() => onOpenResult(row)}
                onDelete={async () => {
                  try {
                    await deleteExpress.mutateAsync(row.id);
                    toast.success("Análise excluída.");
                  } catch (err) {
                    toast.error(
                      err instanceof Error ? err.message : "Erro ao excluir análise.",
                    );
                    throw err;
                  }
                }}
                onRename={async (label) => {
                  await renameExpress.mutateAsync({ id: row.id, patientLabel: label });
                }}
              />
            ))}
          </div>
        </PageStateGate>
      </div>
    </div>
  );
}

// ─── History card ──────────────────────────────────────────────────────────

interface HistoryCardProps {
  row: DecodificarExpressRow;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: (checked: boolean) => void;
  disableActions?: boolean;
  onClick: () => void;
  onDelete: () => Promise<void>;
  onRename: (label: string) => Promise<void>;
}

function HistoryCard({
  row,
  selectionMode,
  selected,
  onToggleSelected,
  disableActions,
  onClick,
  onDelete,
  onRename,
}: HistoryCardProps) {
  const typeId = row.result_type as EnneagramTypeId;
  const accent = TYPE_ACCENT[typeId];
  const content = TYPE_CONTENT[typeId];
  const date = new Date(row.created_at).toLocaleDateString("pt-BR");

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      className="text-left bg-card border border-border/50 rounded-xl p-4 sm:p-5 card-hover cursor-pointer transition-all duration-200 hover:border-primary/60 hover:bg-primary/[0.04] hover:shadow-[0_0_0_1px_rgba(56,189,248,0.18),0_10px_30px_rgba(56,189,248,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70"
    >
      <div className="flex items-center gap-3 sm:gap-4">
        {selectionMode ? (
          <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={selected}
              onCheckedChange={(v) => onToggleSelected(Boolean(v))}
              aria-label="Selecionar análise"
              disabled={disableActions}
            />
          </div>
        ) : null}
        <EnneagramBadge
          type={typeId}
          label={content.name}
          accentColor={accent}
          className="min-w-[104px] shrink-0 px-2.5 py-2.5 sm:min-w-[112px]"
        />
        <div className="min-w-0 flex-1">
          <div onClick={(e) => e.stopPropagation()}>
            <InlineEditText
              value={row.patient_label || ""}
              onSave={onRename}
              placeholder="Paciente sem identificador"
              className="text-base font-medium text-foreground"
              disabled={disableActions}
            />
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Clock weight="light" className="h-3 w-3" />
            {date}
            {row.ambiguous && (
              <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                ambíguo
              </span>
            )}
          </div>
        </div>
        <DeleteEntityButton
          entityLabel="decodificação"
          confirmDescription="A análise será removida do seu histórico permanentemente."
          onConfirm={onDelete}
          disabled={disableActions}
          size="sm"
          className="shrink-0 self-start"
          data-testid={`decodificar-delete-${row.id}`}
        />
      </div>
    </div>
  );
}

// ─── Stats ─────────────────────────────────────────────────────────────────

interface DecodificarStats {
  total: number;
  dominantType: EnneagramTypeId | null;
  dominantCount: number;
  distribution: Array<{ typeId: EnneagramTypeId; count: number; percentage: number }>;
}

function buildDecodificarStats(rows: DecodificarExpressRow[]): DecodificarStats {
  const distribution = (Object.keys(TYPE_CONTENT) as Array<`${EnneagramTypeId}`>)
    .map((k) => Number(k) as EnneagramTypeId)
    .sort((a, b) => a - b)
    .map((typeId) => ({
      typeId,
      count: rows.filter((row) => Number(row.result_type) === typeId).length,
      percentage: 0,
    }));

  const total = rows.length;
  if (total === 0) return { total: 0, dominantType: null, dominantCount: 0, distribution };

  const withPercentages = distribution.map((item) => ({
    ...item,
    percentage: Math.round((item.count / total) * 100),
  }));

  const dominant = withPercentages.reduce(
    (best, current) => (current.count > best.count ? current : best),
    withPercentages[0],
  );

  return {
    total,
    dominantType: dominant.typeId,
    dominantCount: dominant.count,
    distribution: withPercentages.sort((a, b) => b.count - a.count || a.typeId - b.typeId),
  };
}

function DecodificarMiniDashboard({ stats }: { stats: DecodificarStats }) {
  const dominantName = stats.dominantType ? TYPE_CONTENT[stats.dominantType].name : null;
  const dominantPercentage = stats.distribution[0]?.percentage ?? 0;
  const activeTypes = stats.distribution.filter((item) => item.count > 0).length;
  const hasData = stats.total > 0;

  return (
    <Collapsible defaultOpen={false}>
      <div className="bg-card/70 border border-border/50 rounded-xl p-4 sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <h3 className="font-medium text-foreground">Padrões de personalidade</h3>
            <p className="text-sm text-muted-foreground mt-0.5 truncate">
              {hasData
                ? `${dominantPercentage}% do seu público tende ao Código C${stats.dominantType}.`
                : "Expanda para ver os padrões quando houver análises."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="rounded-lg border border-border/40 bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
              <span className="uppercase tracking-wide">Análises</span>{" "}
              <span className="ml-1 font-semibold text-foreground">{stats.total}</span>
            </div>
            <div className="rounded-lg border border-border/40 bg-secondary/20 px-3 py-2 text-xs text-muted-foreground">
              <span className="uppercase tracking-wide">Códigos</span>{" "}
              <span className="ml-1 font-semibold text-foreground">{activeTypes}</span>
            </div>
            <CollapsibleTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="gap-1.5 border-border/60 [&[data-state=open]>svg]:rotate-180"
              >
                Detalhar
                <CaretDown className="h-4 w-4 transition-transform" />
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        <CollapsibleContent className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
          <div className="mt-4 rounded-lg border border-border/40 bg-secondary/10 p-4">
            {stats.total === 0 ? (
              <p className="text-sm text-muted-foreground">
                Sem histórico ainda. Quando você salvar análises, esse painel
                mostrará a frequência por tipo.
              </p>
            ) : (
              <>
                <p className="text-sm text-foreground">
                  {dominantName ? (
                    <>
                      <span className="text-muted-foreground">Maior frequência:</span>{" "}
                      <span className="font-semibold">
                        C{stats.dominantType} - {dominantName}
                      </span>{" "}
                      <span className="text-muted-foreground">
                        ({dominantPercentage}% / {stats.dominantCount} de{" "}
                        {stats.total} análises)
                      </span>
                    </>
                  ) : null}
                </p>
                <div className="mt-3 space-y-2">
                  {stats.distribution
                    .filter((item) => item.count > 0)
                    .map((item) => (
                      <div key={item.typeId} className="flex items-center gap-3">
                        <div className="w-20 shrink-0 text-xs text-muted-foreground">
                          C{item.typeId}
                        </div>
                        <div className="h-2 flex-1 rounded-full bg-secondary/40 overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${Math.max(item.percentage, 4)}%`,
                              backgroundColor: TYPE_ACCENT[item.typeId],
                            }}
                          />
                        </div>
                        <div className="w-24 shrink-0 text-right text-xs text-muted-foreground">
                          {item.percentage}% ({item.count})
                        </div>
                      </div>
                    ))}
                </div>
              </>
            )}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  );
}

// ─── Express quiz flow ─────────────────────────────────────────────────────

interface QuizFlowProps {
  onExit: () => void;
  initialRow?: DecodificarExpressRow | null;
}

function QuizFlow({ onExit, initialRow }: QuizFlowProps) {
  const [answers, setAnswers] = useState<ExpressAnswers>(initialRow?.signals ?? {});
  const [patientLabel, setPatientLabel] = useState(initialRow?.patient_label ?? "");
  const [freeNote, setFreeNote] = useState(initialRow?.free_note ?? "");
  const saveMut = useSaveExpress();
  const updateMut = useUpdateExpress();

  const answeredCount = answeredSignalCount(answers);
  const result = useMemo<ClassifyResult | null>(
    () => (answeredCount > 0 ? classify(answers) : null),
     
    [answers, answeredCount],
  );

  const reset = () => {
    setAnswers({});
    setPatientLabel("");
    setFreeNote("");
  };

  const handleSaveAndExit = async () => {
    if (!result) return;
    try {
      const payload = {
        patientLabel: patientLabel.trim() || null,
        freeNote: freeNote.trim() || null,
        signals: answers,
        result,
      };
      if (initialRow) {
        await updateMut.mutateAsync({ id: initialRow.id, ...payload });
      } else {
        await saveMut.mutateAsync(payload);
      }
      reset();
      onExit();
    } catch {
      // toast already shown by hook
    }
  };

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-foreground">
            Triagem Rápida
          </h2>
          <p className="text-sm text-muted-foreground">
            Observe o paciente e toque o que melhor descreve cada sinal.
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={onExit}>
          ← Voltar
        </Button>
      </div>

      {/* Patient label */}
      <div className="max-w-sm">
        <label
          htmlFor="express-patient-label"
          className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
        >
          Identificação do paciente (opcional)
        </label>
        <Input
          id="express-patient-label"
          value={patientLabel}
          onChange={(e) => setPatientLabel(e.target.value)}
          placeholder="Ex: Paciente JM, sala 2"
        />
      </div>

      {/* Signals + live result */}
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,480px)]">
        {/* Signal chips */}
        <div className="space-y-6 rounded-xl border bg-card p-5">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">
              {answeredCount}/5 sinais respondidos
            </p>
            {answeredCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={reset}
                className="h-7 gap-1 text-xs"
              >
                <ArrowClockwise className="h-3 w-3" />
                Limpar
              </Button>
            )}
          </div>

          <div className="space-y-6">
            {SIGNALS.map((signal) => (
              <SignalChips
                key={signal.id}
                signal={signal}
                selected={answers[signal.id]}
                onSelect={(type) =>
                  setAnswers((prev) => ({ ...prev, [signal.id]: type }))
                }
              />
            ))}
          </div>

          {/* Free note */}
          <div className="border-t pt-4">
            <label
              htmlFor="express-free-note"
              className="block text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5"
            >
              Nota livre (opcional)
            </label>
            <Textarea
              id="express-free-note"
              value={freeNote}
              onChange={(e) => setFreeNote(e.target.value)}
              placeholder="Como foi o atendimento até agora? Algo que chamou atenção?"
              rows={3}
            />
          </div>
        </div>

        {/* Live result — sticky so it follows scroll on desktop */}
        <div className="space-y-4 xl:sticky xl:top-4 xl:self-start">
          {result ? (
            <>
              <PlaybookCard result={result} />
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveAndExit}
                  disabled={saveMut.isPending}
                  className="flex-1 gap-2"
                >
                  <FloppyDisk weight="bold" />
                  {saveMut.isPending ? "Salvando…" : "Salvar e voltar"}
                </Button>
                <Button variant="outline" onClick={onExit}>
                  Descartar
                </Button>
              </div>
            </>
          ) : (
            <div className="flex h-full min-h-[280px] flex-col items-center justify-center rounded-xl border border-dashed bg-card/50 p-8 text-center">
              <Sparkle
                size={32}
                className="mb-3 text-muted-foreground"
                weight="duotone"
              />
              <p className="text-sm font-medium text-foreground">
                Toque pelo menos 1 sinal
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                O playbook aparece aqui em tempo real.
                <br />
                3+ sinais = maior confiança.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Type detail sheet ─────────────────────────────────────────────────────

function TypeDetailContent({ typeId }: { typeId: EnneagramTypeId }) {
  const c = getTypeContent(typeId);
  const accent = TYPE_ACCENT[typeId];
  return (
    <div className="space-y-5">
      <SheetHeader>
        <SheetTitle className="flex items-center gap-3">
          <span
            className="inline-flex items-center justify-center rounded-xl border border-border bg-card text-foreground font-bold text-2xl w-12 h-12"
            style={{ boxShadow: `0 0 0 3px ${accent}25` }}
            aria-hidden="true"
          >
            C{typeId}
          </span>
          <span>{c.name}</span>
        </SheetTitle>
        <SheetDescription>{c.shortDescription}</SheetDescription>
      </SheetHeader>
      <section>
        <h4 className="text-sm font-semibold text-foreground mb-1">
          Como se comunicar
        </h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {c.communicationStyle}
        </p>
      </section>
      <section>
        <h4 className="text-sm font-semibold text-foreground mb-1">
          Abordagem de condução
        </h4>
        <p className="text-sm text-muted-foreground leading-relaxed">
          {c.sellingApproach}
        </p>
      </section>
      <section>
        <h4 className="text-sm font-semibold text-foreground mb-1">
          Frase-chave do paciente
        </h4>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
          {c.objections.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      </section>
      <section>
        <h4 className="text-sm font-semibold text-foreground mb-1">Não faça</h4>
        <ul className="text-sm text-muted-foreground space-y-1 list-disc pl-5">
          {c.doNot.map((o) => (
            <li key={o}>{o}</li>
          ))}
        </ul>
      </section>
    </div>
  );
}

